import React, { forwardRef, useImperativeHandle, useMemo } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

function buildCardOptions(colorScheme: 'dark' | 'light') {
  return {
    style: {
      base: {
        color: colorScheme === 'light' ? '#111827' : '#e5e7eb',
        fontFamily: 'ui-sans-serif, system-ui, sans-serif',
        fontSize: '14px',
        '::placeholder': { color: colorScheme === 'light' ? '#9ca3af' : '#6b7280' },
        iconColor: colorScheme === 'light' ? '#6b7280' : '#9ca3af',
      },
      invalid: {
        color: colorScheme === 'light' ? '#dc2626' : '#f87171',
        iconColor: colorScheme === 'light' ? '#dc2626' : '#f87171',
      },
    },
  };
}

export interface StripeCardFormHandle {
  confirmSetup: (clientSecret: string) => Promise<{ paymentMethodId: string } | { error: string }>;
}

interface InnerProps {
  disabled?: boolean;
  colorScheme?: 'dark' | 'light';
}

const InnerForm = forwardRef<StripeCardFormHandle, InnerProps>(function InnerForm({ disabled, colorScheme = 'dark' }, ref) {
  const stripe = useStripe();
  const elements = useElements();

  useImperativeHandle(ref, () => ({
    async confirmSetup(clientSecret: string) {
      if (!stripe || !elements) return { error: 'Stripe not loaded' };
      const card = elements.getElement(CardElement);
      if (!card) return { error: 'Card element not found' };
      const { setupIntent, error } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: { card },
      });
      if (error) return { error: error.message || 'Card setup failed' };
      const pmId = typeof setupIntent?.payment_method === 'string'
        ? setupIntent.payment_method
        : setupIntent?.payment_method?.id ?? '';
      if (!pmId) return { error: 'No payment method returned' };
      return { paymentMethodId: pmId };
    },
  }));

  const containerClass = colorScheme === 'light'
    ? 'p-3 border border-gray-200 rounded-lg bg-white'
    : 'p-3 border border-dark-border rounded-lg bg-dark-canvas';

  return (
    <div className={containerClass}>
      <CardElement options={{ ...buildCardOptions(colorScheme), disabled }} />
    </div>
  );
});

interface StripeCardFormProps {
  publishableKey: string;
  disabled?: boolean;
  colorScheme?: 'dark' | 'light';
}

const StripeCardForm = forwardRef<StripeCardFormHandle, StripeCardFormProps>(
  function StripeCardForm({ publishableKey, disabled, colorScheme = 'dark' }, ref) {
    const stripePromise = useMemo(
      () => (publishableKey ? loadStripe(publishableKey) : null),
      [publishableKey],
    );

    if (!stripePromise) {
      return (
        <div className="p-3 border border-dark-border rounded-lg bg-dark-canvas text-gray-500 text-sm">
          Payment processing unavailable
        </div>
      );
    }

    return (
      <Elements stripe={stripePromise}>
        <InnerForm ref={ref} disabled={disabled} colorScheme={colorScheme} />
      </Elements>
    );
  },
);

export default StripeCardForm;
